<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import {
  createMatch,
  listMatches,
  reconciliation,
  sumCheck,
  type Match,
  type MatchPayable,
  type MatchTransaction,
} from '../api/reconciliation'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatMoney } from '../lib/money'
import { formatDate } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { locale } = useI18n()
const session = useSession()
const load = useForm()
const action = useForm()

const TABS = ['suggested', 'confirmed', 'manual'] as const
const tab = ref<(typeof TABS)[number]>('suggested')

const matches = ref<Match[]>([])
const cursor = ref<string | undefined>()
const unmatched = ref<{ payables: MatchPayable[]; transactions: MatchTransaction[] }>({ payables: [], transactions: [] })
const txId = ref('')
const picked = ref<string[]>([])
const done = ref(false)

async function reload(append = false) {
  await load.submit(async () => {
    if (tab.value === 'manual') {
      unmatched.value = await call(reconciliation.GET('/unmatched'))
      txId.value = ''
      picked.value = []
      return
    }
    const page = await listMatches(tab.value, append ? cursor.value : undefined)
    matches.value = append ? [...matches.value, ...page.items] : page.items
    cursor.value = page.next_cursor
  })
}
watch([() => session.workspace?.id, tab], () => reload(), { immediate: true })

const drop = (m: Match) => (matches.value = matches.value.filter((x) => x.id !== m.id))
const confirmMatch = (m: Match) =>
  action.submit(async () => {
    await call(reconciliation.POST('/matches/{id}/confirm', { params: { path: { id: m.id } } }))
    drop(m)
  })
const rejectMatch = (m: Match) =>
  action.submit(async () => {
    await call(reconciliation.POST('/matches/{id}/reject', { params: { path: { id: m.id } } }))
    drop(m)
  })

// ---- manual ----
const tx = computed(() => unmatched.value.transactions.find((t) => t.id === txId.value))
const selectedPayables = computed(() => unmatched.value.payables.filter((p) => picked.value.includes(p.id)))
const check = computed(() => (tx.value ? sumCheck(tx.value, selectedPayables.value) : null))
const money = (amount_minor: number) => formatMoney({ amount_minor, currency: tx.value?.amount.currency ?? 'EUR' }, locale.value)

async function onManual() {
  if (!tx.value || !check.value?.ok) return
  done.value = false
  const ok = await action.submit(() => createMatch(tx.value!.id, picked.value))
  if (ok) {
    done.value = true
    await reload()
  }
}

const scorePct = (s: number) => `${Math.round(s * 100)}%`
const scoreVariant = (s: number) => (s >= 0.85 ? 'success' : s >= 0.6 ? 'secondary' : 'outline')
const txLabel = (t: MatchTransaction) => t.counterparty_name || t.remittance || '—'
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('reconciliation.title') }}</h1>
        <p class="muted">{{ $t('reconciliation.subtitle') }}</p>
      </div>
    </header>

    <div class="tabs" role="tablist">
      <button v-for="tb in TABS" :key="tb" role="tab" type="button" :aria-selected="tab === tb" :data-testid="`recon-tab-${tb}`" @click="tab = tb">
        {{ $t(`reconciliation.tabs.${tb}`) }}
      </button>
    </div>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <template v-if="tab !== 'manual'">
      <p v-if="load.busy.value && !matches.length" class="muted">{{ $t('common.loading') }}</p>
      <UiCard v-else-if="!matches.length">
        <EmptyState
          :title="$t(tab === 'suggested' ? 'reconciliation.noSuggestions' : 'reconciliation.noConfirmed')"
          :message="$t('reconciliation.emptyText')"
        />
      </UiCard>
      <UiCard v-for="m in matches" :key="m.id" data-testid="match-card">
        <div class="row head">
          <UiBadge :variant="scoreVariant(m.score)">{{ $t('reconciliation.score') }} {{ scorePct(m.score) }}</UiBadge>
          <span v-if="m.confirmed_at" class="muted small">
            {{ $t(`reconciliation.by.${m.confirmed_by ?? 'user'}`) }} · {{ formatDate(m.confirmed_at) }}
          </span>
          <span class="spacer" />
          <template v-if="m.status === 'suggested' && session.canWrite">
            <UiButton size="sm" :loading="action.busy.value" data-testid="match-confirm" @click="confirmMatch(m)">{{ $t('reconciliation.confirm') }}</UiButton>
            <UiButton size="sm" variant="outline" :disabled="action.busy.value" @click="rejectMatch(m)">{{ $t('reconciliation.reject') }}</UiButton>
          </template>
        </div>
        <div class="sides">
          <div>
            <p class="muted small">{{ $t('reconciliation.transaction') }}</p>
            <strong>{{ txLabel(m.transaction) }}</strong>
            <p v-if="m.transaction.remittance && m.transaction.counterparty_name" class="small muted">{{ m.transaction.remittance }}</p>
            <p class="small">{{ m.transaction.booked_at }} · <span class="out">{{ formatMoney(m.transaction.amount, locale) }}</span></p>
          </div>
          <div>
            <p class="muted small">{{ $t('reconciliation.payables') }}</p>
            <div v-for="p in m.payables" :key="p.id" class="pay">
              <strong>{{ p.vendor }}</strong>
              <span class="small muted">{{ p.invoice_number ?? '' }} {{ p.due_date ? `· ${$t('bills.dueDate')} ${p.due_date}` : '' }}</span>
              <span class="small">{{ formatMoney(p.total, locale) }}</span>
            </div>
          </div>
        </div>
      </UiCard>
      <div v-if="cursor" class="more">
        <UiButton variant="outline" :loading="load.busy.value" @click="reload(true)">{{ $t('transactions.loadMore') }}</UiButton>
      </div>
    </template>

    <template v-else>
      <UiCard v-if="!session.canWrite"><p class="muted">{{ $t('ledger.readOnly') }}</p></UiCard>
      <p v-else-if="load.busy.value" class="muted">{{ $t('common.loading') }}</p>
      <UiCard v-else-if="!unmatched.transactions.length || !unmatched.payables.length">
        <EmptyState :title="$t('reconciliation.nothingUnmatched')" :message="$t('reconciliation.emptyText')" />
      </UiCard>
      <form v-else class="manual" data-testid="manual-match" @submit.prevent="onManual">
        <UiCard :title="$t('reconciliation.pickTransaction')">
          <ul class="list">
            <li v-for="t in unmatched.transactions" :key="t.id">
              <label class="pick">
                <input v-model="txId" type="radio" name="tx" :value="t.id" />
                <span class="grow">
                  <strong>{{ txLabel(t) }}</strong>
                  <span class="small muted">{{ t.booked_at }}</span>
                </span>
                <span class="num out">{{ formatMoney(t.amount, locale) }}</span>
              </label>
            </li>
          </ul>
        </UiCard>
        <UiCard :title="$t('reconciliation.pickPayables')">
          <ul class="list">
            <li v-for="p in unmatched.payables" :key="p.id">
              <label class="pick">
                <input v-model="picked" type="checkbox" :value="p.id" />
                <span class="grow">
                  <strong>{{ p.vendor }}</strong>
                  <span class="small muted">{{ p.invoice_number ?? '' }} {{ p.due_date ?? '' }}</span>
                </span>
                <span class="num">{{ formatMoney(p.total, locale) }}</span>
              </label>
            </li>
          </ul>
        </UiCard>
        <UiCard class="sum">
          <div v-if="check" class="row" data-testid="sum-check">
            <span>{{ $t('reconciliation.selected') }}: <strong>{{ money(check.sum) }}</strong> / {{ money(check.target) }}</span>
            <UiBadge v-if="check.ok" variant="success">{{ $t('reconciliation.sumOk') }}</UiBadge>
            <UiBadge v-else-if="check.currencyMismatch" variant="danger">{{ $t('reconciliation.currencyMismatch') }}</UiBadge>
            <UiBadge v-else-if="picked.length" variant="danger">{{ $t('reconciliation.difference', { amount: money(check.diff) }) }}</UiBadge>
          </div>
          <p v-else class="muted small">{{ $t('reconciliation.pickTransaction') }}</p>
          <div class="row">
            <UiButton type="submit" :disabled="!check?.ok" :loading="action.busy.value">{{ $t('reconciliation.match') }}</UiButton>
            <span v-if="done" class="small in" role="status">{{ $t('reconciliation.matched') }}</span>
          </div>
        </UiCard>
      </form>
    </template>
  </div>
</template>

<style scoped>
.wide {
  max-width: 1200px;
}
.head {
  margin-bottom: 0.75rem;
}
.sides {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
}
.pay {
  display: flex;
  flex-direction: column;
  padding: 0.35rem 0;
  border-top: 1px solid var(--border);
}
.pay:first-of-type {
  border-top: 0;
}
.manual {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}
.manual .sum {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.pick {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  cursor: pointer;
}
.pick input {
  width: auto;
  min-height: 0;
}
.grow {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-break: break-word;
}
.num {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.out {
  color: var(--loss);
}
.in {
  color: var(--gain);
}
.more {
  display: flex;
  justify-content: center;
}
</style>
